import * as React from 'react';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import PersonIcon from '@mui/icons-material/Person';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import LogoutIcon from '@mui/icons-material/Logout';
import GroupIcon from '@mui/icons-material/Group';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useNavigate, Outlet } from 'react-router-dom';
import { CarIcon } from 'lucide-react';

const drawerWidth = 220;

export default function Layout() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token_crm');
    navigate('/login');
  };

  // fetch unread notifications count (uses existing list endpoint; expects result.total)
  React.useEffect(() => {
    let mounted = true;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    const fetchUnreadCount = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_SERVER_URI}/notifications/request`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        const data = await res.json();
        if (!mounted) return;
        const count = data.count || 0;
        setUnreadCount(count);
      } catch (e) {
        // ignore errors
      }
    };

    fetchUnreadCount();
    const id = setInterval(fetchUnreadCount, 30000); // poll every 30s
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  const drawer = (
    <Box sx={{ mt: 2 }}>
      <List>
        <ListItem button onClick={() => { setUnreadCount(0); navigate('/requests'); setMobileOpen(false); }}>
          <ListItemIcon>
            <Badge badgeContent={unreadCount} color="error" invisible={unreadCount === 0}>
              <AssignmentIcon />
            </Badge>
          </ListItemIcon>
          <ListItemText primary="New Order" />
        </ListItem>
        <ListItem button onClick={() => { navigate('/customers'); setMobileOpen(false); }}>
          <ListItemIcon><PersonIcon /></ListItemIcon>
          <ListItemText primary="Washes" />
        </ListItem>
        <ListItem button onClick={() => { navigate('/discrepancies'); setMobileOpen(false); }}>
          <ListItemIcon><AssignmentIcon /></ListItemIcon>
          <ListItemText primary="Discrepancies" />
        </ListItem>
          <ListItem button onClick={() => { navigate('/cancelled'); setMobileOpen(false); }}>
          <ListItemIcon><AssignmentIcon /></ListItemIcon>
          <ListItemText primary="Cancelled Washes" />
        </ListItem>
        <ListItem button onClick={() => { navigate('/completedwash'); setMobileOpen(false); }}>
          <ListItemIcon><AssignmentIcon /></ListItemIcon>
          <ListItemText primary="Completed" />
        </ListItem>
        <ListItem button onClick={() => { navigate('/cars/list'); setMobileOpen(false); }}>
          <ListItemIcon><CarIcon /></ListItemIcon>
          <ListItemText primary="Cars" />
        </ListItem>
        <ListItem button onClick={() => { navigate('/followups'); setMobileOpen(false); }}>
          <ListItemIcon><GroupIcon /></ListItemIcon>
          <ListItemText primary="Flowups" />
        </ListItem>
        {/* <ListItem button onClick={() => { navigate('/ondemand-washes'); setMobileOpen(false); }}>
          <ListItemIcon><FlashOnIcon /></ListItemIcon>
          <ListItemText primary="OnDemand" />
        </ListItem> */}
        <ListItem button onClick={() => { navigate('/washers'); setMobileOpen(false); }}>
          <ListItemIcon><LocalLaundryServiceIcon /></ListItemIcon>
          <ListItemText primary="LAMP" />
        </ListItem>
        <ListItem button onClick={() => { navigate('/users'); setMobileOpen(false); }}>
          <ListItemIcon><GroupIcon /></ListItemIcon>
          <ListItemText primary="Users" />
        </ListItem>
        <ListItem button onClick={() => { navigate('/subscriptions'); setMobileOpen(false); }}>
          <ListItemIcon><SubscriptionsIcon /></ListItemIcon>
          <ListItemText primary="Subscriptions" />
        </ListItem>
        <ListItem button onClick={() => { navigate('/cancelledsubscriptions'); setMobileOpen(false); }}>
          <ListItemIcon><SubscriptionsIcon /></ListItemIcon>
          <ListItemText primary="Cancelled subscription" />
        </ListItem>
        <ListItem button onClick={() => { navigate('/chat'); setMobileOpen(false); }}>
          <ListItemIcon><GroupIcon /></ListItemIcon>
          <ListItemText primary="Chat" />
        </ListItem>
        <ListItem button onClick={() => { navigate('/feedback'); setMobileOpen(false); }}>
          <ListItemIcon><GroupIcon /></ListItemIcon>
          <ListItemText primary="Feedback" />
        </ListItem>
        <ListItem button onClick={() => { navigate('/pending-payments'); setMobileOpen(false); }}>
          <ListItemIcon><GroupIcon /></ListItemIcon>
          <ListItemText primary="Pending Payments" />
        </ListItem>
        {/* <ListItem button onClick={() => { navigate('/notifications'); setMobileOpen(false); }}>
          <ListItemIcon><GroupIcon /></ListItemIcon>
          <ListItemText primary="Notifications" />
        </ListItem> */}
        <ListItem button onClick={() => { navigate('/blogs'); setMobileOpen(false); }}>
          <ListItemIcon><GroupIcon /></ListItemIcon>
          <ListItemText primary="Blogs" />
        </ListItem>
        <ListItem button onClick={() => { navigate('/addnewlamp'); setMobileOpen(false); }}>
          <ListItemIcon><GroupIcon /></ListItemIcon>
          <ListItemText primary="Add New Lamp" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            TMO CRM
          </Typography>
          <IconButton color="inherit" onClick={() => navigate('/notifications')} sx={{ mr: 1 }}>
            <Badge badgeContent={unreadCount} color="error" overlap="circular" invisible={unreadCount === 0}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      {/* Side Drawer for desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
        }}
        open
      >
        <Toolbar />
        {drawer}
      </Drawer>
      {/* Side Drawer for mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        {drawer}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
